"use client"

import React, { useEffect, useMemo, useState } from "react"
import { debounce } from "next/dist/server/utils"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { loadProducts } from "@/actions/productActions"
import {
  addQuoteToBasketLegacyAction,
  changeQuoteRequestToQuoteLegacyAction,
  deleteQuoteRequestLineItemAction,
  updateQuoteRequestAction,
  updateQuoteRequestLineItemQuantityAction,
} from "@/actions/quoteActions"
import { StoryblokServerComponent } from "@storyblok/react/rsc"
import { QuoteRequestLineItemRO, QuoteRequestRO, QuoteRO } from "@ub/quoting-client"
import { DownloadIcon, LoaderCircle, MinusIcon, PlusIcon, SaveIcon, ShoppingCartIcon, TrashIcon } from "lucide-react"
import { toast } from "sonner"

import { formatDate, formatPrice } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { generateBasePriceInfoClient } from "@/lib/price-utils-client"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { Button, Input, Label } from "@/components/ui/components"
import { QuadProductImagesPreview } from "@/components/ui/components/quad-product-images-preview"
import { cn } from "@/components/ui/helpers/utils"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import AccountOrderDetailsLineItemCardClient from "@/components/account/orders/detail/AccountOrderDetailsLineItemCardClient"
import { getQuoteStatus, getStatusColor, QuoteStatus } from "@/components/account/quotes/AccountQuoteMiniCard"
import type { AccountQuoteBlok } from "./types"

interface AccountQuoteClientProps {
  data: any
  isQuote: boolean
  accountContext: AccountContext
  blok: AccountQuoteBlok
}
export default function AccountQuoteClient(props: AccountQuoteClientProps) {
  const isReadOnly = props.isQuote
  const [quoteData, setQuoteData] = useState(props.data)
  const [products, setProducts] = useState<(ReducedProductData | undefined)[] | undefined>(undefined)

  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [creatingQuote, setCreatingQuote] = useState(false)
  const [quoteCreated, setQuoteCreated] = useState(false)
  const status = getQuoteStatus(props.data, !props.isQuote)
  const statusColor = getStatusColor(status)

  const router = useRouter()
  const [addingToBasket, setAddingToBasket] = useState(false)

  const [quantityLoading, setQuantityLoading] = useState("")
  const [deleteLoading, setDeleteLoading] = useState("")
  const debouncedHandleChange = debounce(handleChange, 1000, 10000)

  useEffect(() => {
    const productSkus = !props.isQuote
      ? (quoteData as QuoteRequestRO).items?.map((item) => (item as any).productSKU) || []
      : (quoteData as QuoteRO).items?.map((item) => (item as any).productSKU) || []
    loadProducts(productSkus).then((loadedProducts) => setProducts(loadedProducts))
  }, [quoteData, props.isQuote])

  // TODO: original prices and original quantities arent shown
  const lineItems = useMemo(() => {
    if (props.isQuote) {
      return (
        (quoteData as QuoteRO).items?.map((item, i): OrderLineItemData => {
          const foundProduct = products?.find((p) => p?.id == item.productSKU)

          return {
            productsLoaded: true,
            product: foundProduct,
            orderLineItem: {
              id: `${quoteData.id}_${i}`,
              quantity: {
                value: item.quantity?.value ?? 1,
                unit: item.quantity?.unit,
              },
              product: item.productSKU ?? "",
              pricing: {
                singleBasePrice: { net: item.singlePrice },
                price: { net: item.totalPrice },
                undiscountedSingleBasePrice: { net: item.originSinglePrice },
                undiscountedPrice: { net: item.originTotalPrice },
              },
            },
            basePriceInfo: generateBasePriceInfoClient(
              props.accountContext.isoLocale,
              foundProduct,
              item?.singlePrice?.value ?? 0
            ),
          }
        }) || []
      )
    } else {
      return (
        (quoteData as QuoteRequestRO).items?.map((linkRo, i): OrderLineItemData => {
          const item = linkRo as QuoteRequestLineItemRO
          const foundProduct = products?.find((p) => p?.id == item.productSKU)
          return {
            productsLoaded: true,
            product: foundProduct,
            orderLineItem: {
              id: `${quoteData.id}_${i}`,
              quantity: {
                value: item.quantity?.value ?? 1,
                unit: item.quantity?.unit,
              },
              product: item.productSKU ?? "",
              pricing: {
                singleBasePrice: { net: item.singlePrice },
                price: { net: item.totalPrice },
              },
            },
            basePriceInfo: generateBasePriceInfoClient(
              props.accountContext.isoLocale,
              foundProduct,
              item?.singlePrice?.value ?? 0
            ),
          }
        }) || []
      )
    }
  }, [props.isQuote, props.accountContext.isoLocale, quoteData, products])

  const handleSave = async () => {
    setSaving(true)
    setSaveSuccess(false)
    try {
      await updateQuoteRequestAction(props.accountContext.customerData, props.accountContext.userData, {
        id: quoteData.id,
        displayName: quoteData.displayName || "",
        description: quoteData.description || "",
        items: quoteData.items || [],
      })
      setSaveSuccess(true)
      window.location.reload()
    } finally {
      setSaving(false)
    }
  }

  const handleCreateQuote = async () => {
    setCreatingQuote(true)
    try {
      await changeQuoteRequestToQuoteLegacyAction(
        props.accountContext.customerData,
        props.accountContext.userData,
        quoteData.id
      )
      setQuoteCreated(true)
      router.refresh()
    } finally {
      setCreatingQuote(false)
    }
  }

  const handleAddQuoteToBasket = async () => {
    if (!quoteData?.id) return
    setAddingToBasket(true)
    try {
      const res = await addQuoteToBasketLegacyAction(quoteData.id)
      if (!res) {
        throw new Error("Failed to add quote to basket")
      }
      router.push(getLinkWithPrefixFromURLAndLocale("/basket/overview", props.accountContext.sbLocale))
    } catch (e: any) {
      console.error(e)
      toast.error("Hinzufügen zum Warenkorb fehlgeschlagen.")
    } finally {
      setAddingToBasket(false)
    }
  }

  async function handleIncrease(lineItemId: string, idx: number) {
    if (!lineItemId || !quoteData?.id) return
    const currentItem: any = (quoteData.items || [])[idx]
    const current = Number(currentItem?.quantity?.value || 1)
    const unit = currentItem?.quantity?.unit
    setQuantityLoading(lineItemId)
    try {
      const updated = await updateQuoteRequestLineItemQuantityAction(
        props.accountContext.customerData,
        props.accountContext.userData,
        quoteData.id,
        lineItemId,
        current + 1,
        unit
      )
      if (updated) {
        const updatedItems = [...(quoteData.items || [])]
        if (idx >= 0 && idx < updatedItems.length) {
          // Merge full update so prices and quantity stay in sync
          updatedItems[idx] = { ...updatedItems[idx], ...updated }
          // Recalculate overall total from line totals (fallback to singlePrice * qty)
          const newTotalCurrency = (quoteData.total?.currency || (updatedItems[0] as any)?.totalPrice?.currency) as any
          const newTotalValue = updatedItems.reduce((sum: number, it: any) => {
            const qty = Number(it?.quantity?.value || 1)
            const lineTotal = Number(
              it?.totalPrice?.value ?? (it?.singlePrice?.value != null ? qty * Number(it.singlePrice.value) : 0)
            )
            return sum + (isNaN(lineTotal) ? 0 : lineTotal)
          }, 0)
          setQuoteData({
            ...quoteData,
            items: updatedItems,
            total: { value: newTotalValue, currency: newTotalCurrency },
          })
        }
      }
    } finally {
      setQuantityLoading("")
    }
  }
  async function handleDecrease(lineItemId: string, idx: number) {
    if (!lineItemId || !quoteData?.id) return
    const currentItem: any = (quoteData.items || [])[idx]
    const current = Number(currentItem?.quantity?.value || 1)
    const nextQty = Math.max(1, current - 1)
    const unit = currentItem?.quantity?.unit
    if (nextQty === current) return
    setQuantityLoading(lineItemId)
    try {
      const updated = await updateQuoteRequestLineItemQuantityAction(
        props.accountContext.customerData,
        props.accountContext.userData,
        quoteData.id,
        lineItemId,
        nextQty,
        unit
      )
      if (updated) {
        const updatedItems = [...(quoteData.items || [])]
        if (idx >= 0 && idx < updatedItems.length) {
          // Merge full update so prices and quantity stay in sync
          updatedItems[idx] = { ...updatedItems[idx], ...updated }
          // Recalculate overall total from line totals (fallback to singlePrice * qty)
          const newTotalCurrency = (quoteData.total?.currency || (updatedItems[0] as any)?.totalPrice?.currency) as any
          const newTotalValue = updatedItems.reduce((sum: number, it: any) => {
            const qty = Number(it?.quantity?.value || 1)
            const lineTotal = Number(
              it?.totalPrice?.value ?? (it?.singlePrice?.value != null ? qty * Number(it.singlePrice.value) : 0)
            )
            return sum + (isNaN(lineTotal) ? 0 : lineTotal)
          }, 0)
          setQuoteData({
            ...quoteData,
            items: updatedItems,
            total: { value: newTotalValue, currency: newTotalCurrency },
          })
        }
      }
    } finally {
      setQuantityLoading("")
    }
  }
  async function handleDelete(productId?: string) {
    if (!productId) return
    const items: any[] = quoteData?.items || []
    const idx = items.findIndex((it) => (it as any)?.productSKU === productId)
    if (idx === -1) return

    const lineItem = items[idx]
    const lineItemId: string | undefined = lineItem?.id
    if (!quoteData?.id || !lineItemId) return

    setDeleteLoading(productId)
    try {
      const ok = await deleteQuoteRequestLineItemAction(
        props.accountContext.customerData,
        props.accountContext.userData,
        quoteData.id,
        lineItemId
      )
      if (ok) {
        const updatedItems = items.filter((_, i) => i !== idx)
        const newTotalCurrency = (quoteData.total?.currency || (items[0] as any)?.totalPrice?.currency) as any
        const newTotalValue = updatedItems.reduce((sum: number, it: any) => {
          const qty = Number(it?.quantity?.value || 1)
          const lineTotal = Number(
            it?.totalPrice?.value ?? (it?.singlePrice?.value != null ? qty * Number(it.singlePrice.value) : 0)
          )
          return sum + (isNaN(lineTotal) ? 0 : lineTotal)
        }, 0)
        setQuoteData({ ...quoteData, items: updatedItems, total: { value: newTotalValue, currency: newTotalCurrency } })
        toast.success(props.blok?.label_quote_item_deleted ?? "Position entfernt")
      } else {
        toast.error(props.blok?.label_quote_item_delete_failed ?? "Position konnte nicht entfernt werden")
      }
    } finally {
      setDeleteLoading("")
    }
  }
  async function handleChange(productId: string, inputQuantity: string, input: HTMLInputElement) {
    let quantity = parseInt(inputQuantity, 10)
    if (!productId || isNaN(quantity)) {
      return
    }
    // Ensure minimum quantity of 1
    quantity = Math.max(1, quantity)

    const items: any[] = quoteData?.items || []
    const idx = items.findIndex((it) => (it as any)?.productSKU === productId)
    if (idx === -1) return

    const lineItem = items[idx]
    const lineItemId: string | undefined = lineItem?.id
    if (!quoteData?.id || !lineItemId) return

    const unit = lineItem?.quantity?.unit

    setQuantityLoading(lineItemId)
    try {
      const updated = await updateQuoteRequestLineItemQuantityAction(
        props.accountContext.customerData,
        props.accountContext.userData,
        quoteData.id,
        lineItemId,
        quantity,
        unit
      )
      if (updated) {
        const updatedItems = [...(quoteData.items || [])]
        if (idx >= 0 && idx < updatedItems.length) {
          // Merge full update so prices and quantity stay in sync
          updatedItems[idx] = { ...updatedItems[idx], ...updated }
          // Recalculate overall total from line totals (fallback to singlePrice * qty)
          const newTotalCurrency = (quoteData.total?.currency || (updatedItems[0] as any)?.totalPrice?.currency) as any
          const newTotalValue = updatedItems.reduce((sum: number, it: any) => {
            const qty = Number(it?.quantity?.value || 1)
            const lineTotal = Number(
              it?.totalPrice?.value ?? (it?.singlePrice?.value != null ? qty * Number(it.singlePrice.value) : 0)
            )
            return sum + (isNaN(lineTotal) ? 0 : lineTotal)
          }, 0)
          setQuoteData({
            ...quoteData,
            items: updatedItems,
            total: { value: newTotalValue, currency: newTotalCurrency },
          })
        }
      }
    } finally {
      setQuantityLoading("")
    }
  }

  const getStatusText = (status: QuoteStatus, isQuote: boolean): string | undefined => {
    switch (status) {
      case "EXPIRED":
        return props.blok.label_quote_status_expired
      case "NEW":
        return isQuote ? props.blok.label_quote_status_new : (props.blok.label_quote_status_sent ?? "Gesendet")
      case "RESPONDED":
        return props.blok.label_quote_status_responded
      case "REJECTED":
        return props.blok.label_quote_status_rejected
    }
  }

  if (!props.data) return <div>{props.blok.label_common_not_found}</div>
  return (
    <>
      {/* Header */}
      <AccountBackLinkClient label={props.blok.label_common_back} />
      {/*
      <div className={"flex w-full flex-row justify-end font-bold"}>{props.isQuote ? <>Angebot</> : <>Anfrage</>}</div>
*/}
      <div className={"flex items-center justify-between rounded-sm border border-gray-200 bg-white p-4"}>
        <div>
          <h1 className="flex flex-row flex-wrap gap-x-1 text-xl">
            <span className={"font-light"}>{props.blok.label_quote_process_number + ": "}</span>
            <span className={"font-bold"}>{quoteData.number}</span>
          </h1>
          <p className={"flex flex-row flex-wrap gap-x-1"}>
            <span className={"font-light"}>{(props.blok.label_quote_creation_date ?? "Erstellungsdatum") + ": "}</span>
            <span>
              {formatDate(props.accountContext.isoLocale, new Date(quoteData.creationDate ?? 0), {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </span>
          </p>
          <p className={"flex flex-row flex-wrap gap-x-1"}>
            <span className={"font-light"}>
              {props.blok.label_quote_status}
              {":"}
            </span>
            <span className={`${statusColor} font-bold`}>{getStatusText(status, props.isQuote)}</span>
          </p>
          {isReadOnly && (
            <>
              <p className={"mt-2 flex flex-row flex-wrap gap-x-1"}>
                <span className={"font-light"}>
                  {props.blok.label_quote_reference}
                  {":"}
                </span>
                <span className={""}>{quoteData.displayName}</span>
              </p>
              <p className={"flex flex-row flex-wrap gap-x-1"}>
                <span className={"font-light"}>
                  {props.blok.label_quote_comment}
                  {":"}
                </span>
                <span className={""}>{quoteData.description}</span>
              </p>
            </>
          )}
        </div>
        <QuadProductImagesPreview
          className={"size-32 gap-2 rounded-lg border p-1 *:rounded-lg"}
          productCount={products?.length ?? 0}
          products={products?.filter((i) => i != undefined)}
        />
      </div>

      {!isReadOnly && (
        <div
          className={`
            mt-6 grid grid-cols-1 gap-2 rounded-sm border-gray-300 bg-gray-200 p-4
            max-xl:auto-rows-max
            xl:grid-cols-[1fr_1fr] xl:grid-rows-[auto_max-content]
          `}
        >
          <div className={"flex flex-row flex-wrap items-center justify-start gap-1"}>
            <Label className={"font-light"} htmlFor={"quoteDetailReferenceInput"}>
              {props.blok.label_quote_reference}
              {":"}
            </Label>
            <Input
              id="quoteDetailReferenceInput"
              value={quoteData.displayName || ""}
              onChange={(e) => setQuoteData((q) => ({ ...q, displayName: e.target.value }))}
              placeholder={props.blok.label_quote_reference_placeholder}
            />
          </div>
          <div className={"flex flex-row flex-wrap items-center justify-start gap-1"}>
            <Label className={"font-light"} htmlFor={"quoteDetailCommentInput"}>
              {props.blok.label_quote_comment}
              {":"}
            </Label>
            <Input
              id="quoteDetailCommentInput"
              value={quoteData.description || ""}
              onChange={(e) => setQuoteData((q) => ({ ...q, description: e.target.value }))}
              placeholder={props.blok.label_quote_comment_placeholder}
            />
          </div>
          <div className={"flex flex-row flex-wrap items-center justify-between gap-2 xl:col-span-full xl:justify-end"}>
            <Button
              size={"smNoGrow"}
              variant={"default"}
              onClick={handleCreateQuote}
              disabled={creatingQuote}
              className={"gap-1"}
            >
              {creatingQuote ? (
                <>
                  <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                  <span>{props.blok.label_quote_saving}</span>
                </>
              ) : (
                <>
                  <ShoppingCartIcon className={"inline size-5"} />
                  <span>{props.blok.label_quote_create}</span>
                </>
              )}
            </Button>
            <Button size={"smNoGrow"} variant={"outline"} onClick={handleSave} disabled={saving} className={"gap-1"}>
              {saving ? (
                <>
                  <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                  <span>{props.blok.label_quote_saving}</span>
                </>
              ) : (
                <>
                  <SaveIcon className={"inline size-5"} />
                  <span>{props.blok.label_quote_save_request}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}
      {lineItems.length === 0 ? (
        props.blok.empty_quote_message && (
          <div className="relative z-0 mt-6 rounded-sm border border-gray-200 bg-white p-4">
            {props.blok.empty_quote_message.map((blokInner) => (
              <StoryblokServerComponent key={blokInner._uid} blok={blokInner} />
            ))}
          </div>
        )
      ) : (
        <div className="mt-6 rounded-sm border border-gray-200 bg-white p-4">
          <div
            className="
              mb-4 flex flex-wrap items-center justify-between gap-2 text-xl font-semibold
              sm:flex-row sm:items-center
            "
          >
            <h2>{props.blok.label_quote_details_overview}</h2>
            <div className="flex flex-row flex-wrap gap-2">
              {/*<ReorderAllButtonClient items={(async () => lineItems)()} label={props.blok.label_button_buy_again} />*/}

              {props.blok?.link_pdf_download && props.isQuote && (
                <Link prefetch={false} target={"_blank"} href={(props.blok?.link_pdf_download ?? "") + props.data.id}>
                  <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
                    <DownloadIcon className={"size-5"} />
                  </Button>
                </Link>
              )}

              {/*        <Link prefetch={false} href={props.blok?.link_contact ?? "/info/de-de/kontaktformular"}>
                <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
                  <PhoneIcon className={"size-5"} />
                </Button>
              </Link>*/}
              {props.isQuote && status !== "EXPIRED" && (
                <Button
                  size={"smNoGrow"}
                  variant={"outline"}
                  className="gap-1 bg-primary text-white"
                  onClick={handleAddQuoteToBasket}
                  disabled={addingToBasket}
                >
                  {addingToBasket ? (
                    <>
                      <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                      <span>Wird hinzugefügt...</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCartIcon className={"inline size-5"} />
                      <span>In den Warenkorb</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          <div
            className="
              grid grid-cols-[max-content_auto_max-content_max-content] items-center gap-2 border-b pb-2
              lg:grid-cols-[max-content_max-content_auto_max-content_max-content_max-content] lg:gap-4
            "
          >
            <div
              className={
                "col-span-full grid w-full grid-cols-subgrid grid-rows-1 text-xs font-semibold hyphens-auto lg:text-sm"
              }
            >
              <div className={"max-lg:hidden"}></div>
              <div>{props.blok.label_artnr}</div>
              <div>{props.blok.label_article_column}</div>
              <div>{props.blok.label_quantity}</div>
              <div className={"text-right max-lg:hidden"}>{props.blok.label_price_per_item}</div>
              <div className={"text-right"}>{props.blok.label_total_price_column}</div>
            </div>
            {lineItems.map((item, idx) => (
              <AccountOrderDetailsLineItemCardClient
                key={item.orderLineItem.id}
                item={item}
                isoLocale={props.accountContext.isoLocale}
                sbLocale={props.accountContext.sbLocale}
                quantityElement={
                  isReadOnly ? undefined : (
                    <>
                      <div className={"flex flex-row gap-2"}>
                        <div
                          className={cn(
                            "flex w-auto min-w-12 flex-row items-center justify-center rounded-lg bg-gray-200 p-0 px-2"
                            /*,quantityLoading != "" ? "bg-gray-50" : ""*/
                          )}
                        >
                          <button
                            className={cn(
                              `
                                w-full rounded-t-lg py-0.5
                                max-lg:flex max-lg:justify-center
                                lg:w-auto lg:rounded-full lg:py-1 lg:pr-2 lg:pl-4
                              `
                            )}
                            onClick={() => handleDecrease(((quoteData.items || [])[idx] as any)?.id as string, idx)}
                            /*  disabled={quantityLoading != ""}*/
                            aria-label={props.blok.label_product_quantity_decrease}
                          >
                            <MinusIcon
                              className={cn(
                                "size-5"
                                /*,
                                {
                                "text-muted-foreground opacity-20": quantityLoading != "",
                              }*/
                              )}
                            />
                          </button>
                          <input
                            type="text"
                            pattern="[0-9]*"
                            inputMode={"numeric"}
                            aria-label={props.blok.label_product_quantity}
                            /*disabled={quantityLoading != ""}*/
                            className={cn(
                              `
                                h-9 w-12 border-transparent bg-transparent p-0 py-1 text-center text-base font-bold
                                focus:rounded-md focus:border-2 focus:border-blue-400 focus:ring-transparent
                                focus:outline-hidden
                                lg:text-lg
                              `
                              /*,{ "text-muted-foreground opacity-20": quantityLoading != "" }*/
                            )}
                            defaultValue={item.orderLineItem.quantity.value}
                            onChange={(e) => debouncedHandleChange(item.product?.id ?? "", e.target.value, e.target)}
                            onKeyDown={(event) => {
                              if (
                                !(
                                  /[0-9]/.test(event.key) ||
                                  event.code === "Backspace" ||
                                  event.code === "Delete" ||
                                  event.code === "Delete" ||
                                  event.code === "ArrowLeft" ||
                                  event.code === "ArrowRight"
                                )
                              ) {
                                event.preventDefault()
                              }
                            }}
                          />
                          <button
                            className={cn(
                              `
                                w-full rounded-b-lg py-0.5
                                max-lg:flex max-lg:justify-center
                                lg:w-auto lg:rounded-full lg:py-1 lg:pr-2 lg:pl-4
                              `
                            )}
                            onClick={() => handleIncrease(((quoteData.items || [])[idx] as any)?.id as string, idx)}
                            /*disabled={quantityLoading != ""}*/
                            aria-label={props.blok.label_product_quantity_increase}
                          >
                            <PlusIcon
                              className={cn(
                                "size-5"
                                /*  , { "text-muted-foreground opacity-20": quantityLoading != "" }*/
                              )}
                            />
                          </button>
                        </div>
                        <div className={"flex items-center justify-center"}>
                          <Button
                            onClick={() => handleDelete(item.product?.id)}
                            variant={"destructive"}
                            size={"iconNoGrow"}
                            disabled={deleteLoading == item.product?.id}
                          >
                            {deleteLoading == item.product?.id ? (
                              <LoaderCircle className={"size-5 motion-safe:animate-spin"} />
                            ) : (
                              <TrashIcon className={"size-5"} />
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  )
                }
              />
            ))}
          </div>

          {/* Summary */}
          <div className="mt-4 flex flex-col items-end gap-1">
            <div className="flex gap-8">
              <div>{props.blok.label_quote_total_net}:</div>
              <div className="font-bold">
                {/* Show strikethrough list price total when available */}
                {(() => {
                  try {
                    const discounted = {
                      value: Number(quoteData?.total?.value ?? 0),
                      currency:
                        (quoteData?.total?.currency as any) ??
                        (lineItems[0]?.orderLineItem?.pricing?.price?.net?.currency as any),
                    }
                    // Sum undiscounted totals from line items when present
                    const undiscountedSum = lineItems.reduce((sum, it) => {
                      const v = Number(it?.orderLineItem?.pricing?.undiscountedPrice?.net?.value ?? 0)
                      return sum + (isNaN(v) ? 0 : v)
                    }, 0)
                    const epsilon = 1e-6
                    const showUndiscounted = undiscountedSum > 0 && discounted.value + epsilon < undiscountedSum
                    return showUndiscounted ? (
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-gray-400 line-through">
                          {formatPrice(
                            props.accountContext.isoLocale,
                            undiscountedSum,
                            (lineItems[0]?.orderLineItem?.pricing?.undiscountedPrice?.net?.currency as any) ??
                              discounted.currency
                          )}
                        </span>
                        <span>
                          {formatPrice(props.accountContext.isoLocale, discounted.value, discounted.currency)}
                        </span>
                      </div>
                    ) : (
                      formatPrice(props.accountContext.isoLocale, discounted.value, discounted.currency)
                    )
                  } catch {
                    return formatPrice(
                      props.accountContext.isoLocale,
                      quoteData.total?.value,
                      quoteData.total?.currency
                    )
                  }
                })()}
              </div>
            </div>
          </div>
          {!isReadOnly && (
            <div className={"mt-4 flex flex-row flex-wrap items-center justify-between gap-2 lg:justify-end"}>
              <Button
                size={"smNoGrow"}
                variant={"default"}
                onClick={handleCreateQuote}
                disabled={creatingQuote}
                className={"gap-1"}
              >
                {creatingQuote ? (
                  <>
                    <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                    <span>{props.blok.label_quote_saving}</span>
                  </>
                ) : (
                  <>
                    <ShoppingCartIcon className={"inline size-5"} />
                    <span>{props.blok.label_quote_create}</span>
                  </>
                )}
              </Button>
              <Button size={"smNoGrow"} variant={"outline"} onClick={handleSave} disabled={saving} className={"gap-1"}>
                {saving ? (
                  <>
                    <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                    <span>{props.blok.label_quote_saving}</span>
                  </>
                ) : (
                  <>
                    <SaveIcon className={"inline size-5"} />
                    <span>{props.blok.label_quote_save_request}</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  )
}
